// src/views/pages/restaurant-reservations/waitlist/components/QueueCard.jsx
import React, { useMemo, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Row,
  Space,
  Typography,
  Divider,
  Tag,
  Alert,
  List,
  Button,
  Tooltip,
  Dropdown,
  Progress,
  Segmented,
  Drawer,
  Input,
  Badge,
} from "antd";
import { AnimatePresence, motion } from "framer-motion";
import moment from "moment";
import {
  Bell,
  User as UserIcon,
  Phone as PhoneIcon,
  Users,
  Clock,
  MapPin,
  CheckCircle,
  MoreVertical,
  DollarSign,
  Calendar as CalendarIcon,
  Plus,
  Search as SearchIcon,
  Clipboard,
  X,
  Filter,
  LogIn,
} from "react-feather";

import { calcActualWaitMin, tableStatusPill, pill } from "../utils";

const { Title, Text } = Typography;

export function EmptyState({ title = "No entries", subtitle = "Add a walk-in or search a booking.", onAdd }) {
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} style={{ textAlign: "center", padding: "44px 0" }}>
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 18,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f3f4f6",
          marginBottom: 12,
        }}
      >
        <CalendarIcon size={22} />
      </div>
      <div style={{ fontSize: 16, fontWeight: 900, color: "#111827" }}>{title}</div>
      <div style={{ color: "#6b7280", marginTop: 6 }}>{subtitle}</div>
      {onAdd ? (
        <div style={{ marginTop: 14 }}>
          <Button type="primary" icon={<Users size={16} />} onClick={onAdd} size="large" style={{ borderRadius: 14 }}>
            Add Walk-in
          </Button>
        </div>
      ) : null}
    </motion.div>
  );
}

EmptyState.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  onAdd: PropTypes.func,
};

const normalizeStatus = (s) => String(s || "").trim().toLowerCase();

const queueStatusPill = (status) => {
  const s = normalizeStatus(status);
  if (s === "waiting") return pill("orange", "WAITING");
  if (s === "notified") return pill("blue", "NOTIFIED");
  if (s === "seated") return pill("green", "SEATED");
  if (s === "canceled" || s === "cancelled") return pill("default", "CANCELED");
  return pill("default", s ? s.toUpperCase() : "UNKNOWN");
};

const safeName = (b) => b?.name || b?.guest_name || b?.customerName || "Guest";
const safePhone = (b) => b?.phone || b?.contact_number || "";
const safeParty = (b) => b?.partySize ?? b?.party_size ?? "-";

function getBookingTimeLabel(b) {
  // Reservation listing can be ISO or HH:mm:ss
  if (b?.time && moment(b.time, moment.ISO_8601, true).isValid()) return moment(b.time).format("h:mm A");
  if (b?.reservation_time) return String(b.reservation_time).slice(0, 5);
  if (b?.time) return String(b.time);
  return "—";
}

function QueueCard({
  search,
  matchingBookings,
  onSelectBooking,
  getTableById,
  waitlist,
  openAdd,
  openDetails,
  notifyGuest,
  WaitlistRowActions,
  estimateWaitAI,

  // ✅ Upcoming Drawer (optional)
  upcomingBookings = [],
  onAddBookingToQueue,
  onOpenBooking,
  onMarkBookingArrived, // optional: (booking) => void/Promise
}) {
  // --------------------------------
  // iPad max considered "compact"
  // --------------------------------
  const [isCompact, setIsCompact] = useState(false);
  useEffect(() => {
    const onResize = () => setIsCompact(window.innerWidth <= 1024);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // --------------------------------
  // Tabs
  // --------------------------------
  const [tab, setTab] = useState("waiting"); // waiting | notified | seated

  // --------------------------------
  // Upcoming Drawer
  // --------------------------------
  const [upOpen, setUpOpen] = useState(false);
  const [upQ, setUpQ] = useState("");
  const [upFilter, setUpFilter] = useState("all"); // all | unassigned | assigned

  const drawerPlacement = isCompact ? "bottom" : "right";
  const canShowUpcoming = Array.isArray(upcomingBookings) && upcomingBookings.length > 0;

  // --------------------------------
  // Waitlist sorting + filtering
  // --------------------------------
  const sortedWaitlist = useMemo(() => {
    const list = Array.isArray(waitlist) ? waitlist : [];
    const active = list.filter((w) => !["canceled", "cancelled"].includes(normalizeStatus(w.status)));

    return [...active].sort((a, b) => {
      const pri = (x) => {
        const booked = x.fromBooking ? 1000 : 0;
        const paid = x.paidSkip ? 600 : 0;
        const vip = x.isVIP ? 250 : 0;
        const t = x.joinTime ? moment(x.joinTime).valueOf() : 0;
        return booked + paid + vip - t / 1000000000;
      };
      return pri(b) - pri(a);
    });
  }, [waitlist]);

  const counts = useMemo(() => {
    const c = { waiting: 0, notified: 0, seated: 0 };
    sortedWaitlist.forEach((w) => {
      const s = normalizeStatus(w.status);
      if (c[s] !== undefined) c[s] += 1;
    });
    return c;
  }, [sortedWaitlist]);

  const tabbedList = useMemo(() => sortedWaitlist.filter((w) => normalizeStatus(w.status) === tab), [sortedWaitlist, tab]);

  const nextUp = useMemo(() => {
    if (tab === "seated") return null;
    return tabbedList[0] || null;
  }, [tab, tabbedList]);

  // --------------------------------
  // Actions menu
  // --------------------------------
  const menuForRow = (item) => {
    try {
      const m = WaitlistRowActions?.(item);
      return m && m.items ? m : { items: [] };
    } catch {
      return { items: [] };
    }
  };

  // --------------------------------
  // ETA helpers
  // --------------------------------
  const getEta = useCallback(
    (item) => {
      const serverEta = Number(item.estimatedWait || 0);
      if (serverEta) return serverEta;
      const ai = estimateWaitAI ? Number(estimateWaitAI(item.partySize)) : 0;
      return ai || 0;
    },
    [estimateWaitAI]
  );

  const MobileTimePill = ({ item }) => {
    const actual = calcActualWaitMin(item.joinTime);
    const eta = getEta(item);
    const overrun = eta && actual > eta;

    if (!eta) {
      return (
        <Tag className="qc-chip" style={{ borderRadius: 999 }}>
          <Clock size={12} style={{ marginRight: 6, verticalAlign: -2 }} />
          {actual}m
        </Tag>
      );
    }
    if (overrun) {
      return (
        <Tag color="red" className="qc-chip" style={{ borderRadius: 999, fontWeight: 900 }}>
          Overrun +{actual - eta}m
        </Tag>
      );
    }
    return (
      <Tag color="blue" className="qc-chip" style={{ borderRadius: 999, fontWeight: 900 }}>
        ETA {eta}m
      </Tag>
    );
  };
  MobileTimePill.propTypes = { item: PropTypes.object.isRequired };

  // --------------------------------
  // Booking chip (small)
  // --------------------------------
  const BookingArrivalChip = ({ item }) => {
    const bookingId = item.bookingId || item.booking_id || null;
    if (!item.fromBooking && !bookingId) return null;

    return (
      <Tag className="qc-chip qc-chip-booking" style={{ borderRadius: 999 }}>
        <CalendarIcon size={12} style={{ marginRight: 6, verticalAlign: -2 }} />
        Reservation {bookingId ? `#${bookingId}` : ""}
      </Tag>
    );
  };
  BookingArrivalChip.propTypes = { item: PropTypes.object.isRequired };

  // --------------------------------
  // Upcoming Drawer data
  // --------------------------------
  const upcomingList = useMemo(() => {
    const list = Array.isArray(upcomingBookings) ? upcomingBookings : [];
    const q = String(upQ || "").trim().toLowerCase();

    const filtered = list
      .filter((b) => {
        const status = String(b.status || "").toLowerCase();
        if (status.includes("cancel")) return false;
        return true;
      })
      .filter((b) => {
        if (!q) return true;
        const name = String(safeName(b)).toLowerCase();
        const phone = String(safePhone(b)).toLowerCase();
        const id = String(b.id || "").toLowerCase();
        return name.includes(q) || phone.includes(q) || id.includes(q);
      })
      .filter((b) => {
        if (upFilter === "all") return true;
        const tableId = b.tableId ?? b.assigned_table_id ?? "unassigned";
        const isAssigned = String(tableId) !== "unassigned" && String(tableId) !== "" && tableId !== null;
        if (upFilter === "assigned") return isAssigned;
        if (upFilter === "unassigned") return !isAssigned;
        return true;
      });

    return filtered.sort((a, b) => {
      const ta = getBookingTimeLabel(a);
      const tb = getBookingTimeLabel(b);

      // try parse "h:mm A" by converting to today string
      const nowDate = moment().format("YYYY-MM-DD");
      const pa = moment(`${nowDate} ${ta}`, ["YYYY-MM-DD h:mm A", "YYYY-MM-DD HH:mm", "YYYY-MM-DD HH:mm:ss"], true);
      const pb = moment(`${nowDate} ${tb}`, ["YYYY-MM-DD h:mm A", "YYYY-MM-DD HH:mm", "YYYY-MM-DD HH:mm:ss"], true);

      const va = pa.isValid() ? pa.valueOf() : 0;
      const vb = pb.isValid() ? pb.valueOf() : 0;
      return va - vb;
    });
  }, [upcomingBookings, upQ, upFilter]);

  const upCounts = useMemo(() => {
    const list = Array.isArray(upcomingBookings) ? upcomingBookings : [];
    let assigned = 0;
    let unassigned = 0;
    list.forEach((b) => {
      const tableId = b.tableId ?? b.assigned_table_id ?? "unassigned";
      const isAssigned = String(tableId) !== "unassigned" && String(tableId) !== "" && tableId !== null;
      if (isAssigned) assigned += 1;
      else unassigned += 1;
    });
    return { total: list.length, assigned, unassigned };
  }, [upcomingBookings]);

  const showMatching = search && matchingBookings.length > 0;

  return (
    <>
      {/* Matching Bookings block (existing) */}
      {showMatching ? (
        <Card
          style={{
            borderRadius: 16,
            border: "1px solid #eef2f7",
            marginBottom: 12,
            boxShadow: "0 10px 30px rgba(17,24,39,.04)",
          }}
          bodyStyle={{ padding: 12 }}
        >
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Title level={5} style={{ margin: 0 }}>
              Matching Bookings
            </Title>
            <Text type="secondary">Click one to check-in or add to queue</Text>
          </Space>

          <Divider style={{ margin: "10px 0" }} />

          <Row gutter={[10, 10]}>
            <AnimatePresence>
              {matchingBookings.slice(0, 6).map((b) => {
                const tb = getTableById(b.tableId);
                return (
                  <motion.div
                    key={b.id}
                    style={{ width: "100%" }}
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.22 }}
                  >
                    <Card
                      hoverable
                      style={{ borderRadius: 14, border: "1px solid #eef2f7", marginBottom: 10 }}
                      onClick={() => onSelectBooking(b)}
                    >
                      <Space direction="vertical" style={{ width: "100%" }} size={6}>
                        <Space style={{ width: "100%", justifyContent: "space-between" }}>
                          <Text strong style={{ fontSize: 15 }}>
                            {safeName(b)} {b.isVIP ? <Tag color="gold">VIP</Tag> : null}
                          </Text>
                          <Tag>{b.id}</Tag>
                        </Space>

                        <Space wrap>
                          {pill("blue", `${safeParty(b)} guests`)}
                          {pill("default", getBookingTimeLabel(b))}
                          {tb ? tableStatusPill(tb.status) : pill("default", "Unassigned")}
                        </Space>

                        <Text type="secondary">
                          <PhoneIcon size={14} style={{ marginRight: 6 }} />
                          {safePhone(b) || "No phone"}
                        </Text>

                        {b.notes ? <Alert type="info" showIcon message={b.notes} style={{ borderRadius: 12 }} /> : null}
                      </Space>
                    </Card>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </Row>
        </Card>
      ) : null}

      {/* Queue */}
      <Card
        style={{
          borderRadius: 16,
          border: "1px solid #eef2f7",
          boxShadow: "0 10px 30px rgba(17,24,39,.04)",
        }}
        bodyStyle={{ padding: 12 }}
      >
        {/* Header */}
        <div className="qc-head">
          <div style={{ minWidth: 0 }}>
            <Title level={5} style={{ margin: 0 }}>
              Queue
            </Title>
            <div className="qc-sub">Work by stage. Waiting shows active line; Seated is history.</div>
          </div>

          <div className="qc-right">
            {canShowUpcoming ? (
              <Badge count={upCounts.total} size="small" offset={[-6, 4]}>
                <Button
                  onClick={() => setUpOpen(true)}
                  icon={<CalendarIcon size={16} />}
                  style={{ borderRadius: 12 }}
                >
                  Upcoming
                </Button>
              </Badge>
            ) : null}

            <Button onClick={openAdd} icon={<Plus size={16} />} type="primary" style={{ borderRadius: 12 }}>
              Walk-in
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ marginTop: 10 }}>
          <Segmented
            block={isCompact}
            value={tab}
            onChange={(v) => setTab(String(v))}
            options={[
              {
                value: "waiting",
                label: (
                  <Space size={6}>
                    <Clock size={14} />
                    <span>Waiting</span>
                    <Tag className="qc-miniCount">{counts.waiting}</Tag>
                  </Space>
                ),
              },
              {
                value: "notified",
                label: (
                  <Space size={6}>
                    <Bell size={14} />
                    <span>Notified</span>
                    <Tag className="qc-miniCount">{counts.notified}</Tag>
                  </Space>
                ),
              },
              {
                value: "seated",
                label: (
                  <Space size={6}>
                    <CheckCircle size={14} />
                    <span>Seated</span>
                    <Tag className="qc-miniCount">{counts.seated}</Tag>
                  </Space>
                ),
              },
            ]}
          />
        </div>

        <Divider style={{ margin: "10px 0" }} />

        {/* Next Up */}
        {nextUp ? (
          <div className="qc-nextUp">
            <Space wrap size={8}>
              <Tag className="qc-chip qc-chip-strong">Next</Tag>
              {nextUp.fromBooking ? <Tag color="green" className="qc-chip">BOOKED</Tag> : <Tag className="qc-chip">WALK-IN</Tag>}
              {nextUp.isVIP ? <Tag color="gold" className="qc-chip">VIP</Tag> : null}
              {nextUp.paidSkip ? <Tag color="purple" className="qc-chip">PAID SKIP</Tag> : null}

              <Text strong style={{ fontSize: 14 }}>{nextUp.name || `Guest #${nextUp.id}`}</Text>
              <Tag color="blue" className="qc-chip">{nextUp.partySize || "-"} guests</Tag>
              {!isCompact && nextUp.phone ? <Text type="secondary">{nextUp.phone}</Text> : null}
            </Space>

            <Button type="primary" style={{ borderRadius: 12 }} onClick={() => openDetails(nextUp)}>
              Open
            </Button>
          </div>
        ) : null}

        {/* List */}
        {tabbedList.length === 0 ? (
          <EmptyState
            title={tab === "seated" ? "No seated guests" : "No guests here"}
            subtitle={tab === "seated" ? "Seated guests appear here for history & audit." : "Add a walk-in or check-in a reservation."}
            onAdd={tab !== "seated" ? openAdd : undefined}
          />
        ) : (
          <List
            dataSource={tabbedList}
            renderItem={(item) => {
              const actual = calcActualWaitMin(item.joinTime);
              const eta = getEta(item);
              const pct = eta ? Math.min(Math.round((actual / eta) * 100), 140) : 0;
              const overrun = eta && actual > eta;

              const showNotify = tab !== "seated";
              const showSeat = tab === "notified";

              return (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.18 }}>
                  <List.Item
                    className="qc-item"
                    onClick={() => openDetails(item)}
                    actions={[
                      showNotify ? (
                        <Tooltip title="Notify ready" key="n">
                          <Button
                            className="qc-actionBtn"
                            icon={<Bell size={16} />}
                            disabled={tab === "seated"}
                            onClick={(e) => {
                              e.stopPropagation();
                              notifyGuest(item);
                            }}
                            size={isCompact ? "middle" : "large"}
                          />
                        </Tooltip>
                      ) : null,
                      showSeat ? (
                        <Tooltip title="Seat now" key="s">
                          <Button
                            className="qc-actionBtn"
                            type="primary"
                            icon={<CheckCircle size={16} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              openDetails(item);
                            }}
                            size={isCompact ? "middle" : "large"}
                          />
                        </Tooltip>
                      ) : null,
                      <Dropdown key="d" menu={menuForRow(item)} trigger={["click"]}>
                        <Button className="qc-actionBtn" icon={<MoreVertical size={16} />} onClick={(e) => e.stopPropagation()} size={isCompact ? "middle" : "large"} />
                      </Dropdown>,
                    ].filter(Boolean)}
                  >
                    <List.Item.Meta
                      avatar={
                        <div className="qc-avatar" style={{ background: item.isVIP ? "#f59e0b" : "#e5e7eb" }}>
                          {item.name ? item.name.charAt(0).toUpperCase() : <UserIcon size={18} />}
                        </div>
                      }
                      title={
                        <div className="qc-titleRow">
                          <div className="qc-titleLeft">
                            <Text strong className="qc-name">{item.name || `Guest #${item.id}`}</Text>

                            {item.isVIP ? <Tag color="gold" className="qc-chip">VIP</Tag> : null}
                            {item.fromBooking ? <Tag color="green" className="qc-chip">Booked</Tag> : null}
                            {item.paidSkip ? (
                              <Tag color="purple" className="qc-chip" style={{ fontWeight: 900 }}>
                                Paid <DollarSign size={12} style={{ marginLeft: 6, verticalAlign: -2 }} />
                              </Tag>
                            ) : null}

                            {queueStatusPill(item.status)}
                            <Tag color="blue" className="qc-chip">{item.partySize} guests</Tag>
                          </div>

                          {isCompact ? (
                            <Tag className="qc-chip" style={{ borderRadius: 999 }}>
                              <Clock size={12} style={{ marginRight: 6, verticalAlign: -2 }} />
                              {actual}m
                            </Tag>
                          ) : null}
                        </div>
                      }
                      description={
                        <Space direction="vertical" size={6} style={{ width: "100%" }}>
                          <div className="qc-metaRow">
                            {!isCompact ? (
                              <Text type="secondary" className="qc-meta">
                                <PhoneIcon size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
                                {item.phone || "No phone"}
                              </Text>
                            ) : null}

                            <Text type="secondary" className="qc-meta">
                              <MapPin size={12} style={{ marginRight: 6, verticalAlign: -2 }} />
                              {item.location || "No preference"}
                            </Text>

                            <Text type="secondary" className="qc-meta">
                              <Clock size={12} style={{ marginRight: 6, verticalAlign: -2 }} />
                              Joined {item.joinTime ? moment(item.joinTime).format("h:mm A") : "—"}
                            </Text>
                          </div>

                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <BookingArrivalChip item={item} />
                          </div>

                          {!isCompact ? (
                            <Tooltip title="ETA uses server ETA if available; otherwise AI fallback.">
                              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                                <div style={{ width: 220 }}>
                                  <Progress percent={pct} showInfo={false} status={overrun ? "exception" : "active"} />
                                </div>
                                <Text>
                                  ETA <b>{eta ? `${eta}m` : "N/A"}</b> • Actual <b>{actual}m</b>
                                </Text>
                                {overrun ? <Tag color="red" className="qc-chip">Overrun</Tag> : null}
                              </div>
                            </Tooltip>
                          ) : null}

                          {item.notes ? (
                            <Alert
                              type={String(item.notes).toLowerCase().includes("allergy") ? "error" : "info"}
                              showIcon
                              message={item.notes}
                              style={{ borderRadius: 12 }}
                            />
                          ) : null}
                        </Space>
                      }
                    />
                  </List.Item>
                </motion.div>
              );
            }}
          />
        )}

        {/* ============================
            ✅ Rich Upcoming Drawer / Sheet
            ============================ */}
        <Drawer
          open={upOpen}
          onClose={() => setUpOpen(false)}
          placement={drawerPlacement}
          height={drawerPlacement === "bottom" ? "80vh" : undefined}
          width={drawerPlacement === "right" ? 460 : undefined}
          closeIcon={<X size={18} />}
          title={
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
              <Space size={10}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 14,
                    background: "linear-gradient(180deg,#ffffff 0%, #fbfcfe 100%)",
                    border: "1px solid #eef2f7",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 10px 20px rgba(17,24,39,.05)",
                  }}
                >
                  <CalendarIcon size={18} />
                </div>
                <div style={{ lineHeight: 1.1 }}>
                  <div style={{ fontWeight: 900 }}>Upcoming Reservations</div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>Search arrivals, open details, or add to queue</div>
                </div>
              </Space>

              <Button onClick={() => setUpOpen(false)} icon={<X size={16} />} style={{ borderRadius: 12 }}>
                Close
              </Button>
            </div>
          }
        >
          {/* Sticky Controls */}
          <div className="up-sticky">
            <div className="up-controls">
              <Input
                value={upQ}
                onChange={(e) => setUpQ(e.target.value)}
                allowClear
                prefix={<SearchIcon size={16} />}
                placeholder="Search name / phone / booking id…"
                style={{ borderRadius: 12, height: 40 }}
              />

              <Segmented
                value={upFilter}
                onChange={(v) => setUpFilter(String(v))}
                options={[
                  {
                    value: "all",
                    label: (
                      <Space size={6}>
                        <Filter size={14} />
                        <span>All</span>
                        <Tag className="qc-miniCount">{upCounts.total}</Tag>
                      </Space>
                    ),
                  },
                  {
                    value: "unassigned",
                    label: (
                      <Space size={6}>
                        <MapPin size={14} />
                        <span>Unassigned</span>
                        <Tag className="qc-miniCount">{upCounts.unassigned}</Tag>
                      </Space>
                    ),
                  },
                  {
                    value: "assigned",
                    label: (
                      <Space size={6}>
                        <CheckCircle size={14} />
                        <span>Assigned</span>
                        <Tag className="qc-miniCount">{upCounts.assigned}</Tag>
                      </Space>
                    ),
                  },
                ]}
                block={isCompact}
              />
            </div>

            {!onAddBookingToQueue ? (
              <Alert
                type="info"
                showIcon
                message="Tip: Wire onAddBookingToQueue to enable one-click “Add to queue” from Upcoming."
                style={{ borderRadius: 12 }}
              />
            ) : null}
          </div>

          <Divider style={{ margin: "10px 0" }} />

          {/* Upcoming list */}
          {upcomingList.length === 0 ? (
            <div style={{ padding: 18, borderRadius: 14, border: "1px dashed #e5e7eb", background: "#fbfcfe", textAlign: "center" }}>
              <Text type="secondary">No upcoming reservations match your filters.</Text>
            </div>
          ) : (
            <List
              dataSource={upcomingList.slice(0, 80)}
              renderItem={(b) => {
                const name = safeName(b);
                const phone = safePhone(b);
                const party = safeParty(b);
                const timeLabel = getBookingTimeLabel(b);

                const tableId = b.tableId ?? b.assigned_table_id ?? "unassigned";
                const isAssigned = String(tableId) !== "unassigned" && String(tableId) !== "" && tableId !== null;

                return (
                  <List.Item
                    className="up-item"
                    onClick={() => (onOpenBooking ? onOpenBooking(b) : openDetails?.(b))}
                    actions={[
                      <Tooltip title="Open details" key="open">
                        <Button
                          icon={<Clipboard size={16} />}
                          style={{ borderRadius: 12 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            (onOpenBooking ? onOpenBooking(b) : openDetails?.(b));
                          }}
                        />
                      </Tooltip>,

                      onMarkBookingArrived ? (
                        <Tooltip title="Mark arrived (check-in)" key="arr">
                          <Button
                            icon={<LogIn size={16} />}
                            style={{ borderRadius: 12 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onMarkBookingArrived?.(b);
                            }}
                          />
                        </Tooltip>
                      ) : null,

                      <Tooltip title={onAddBookingToQueue ? "Add to queue" : "Wire onAddBookingToQueue"} key="add">
                        <Button
                          type="primary"
                          icon={<Plus size={16} />}
                          style={{ borderRadius: 12 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onAddBookingToQueue?.(b);
                          }}
                          disabled={!onAddBookingToQueue}
                        />
                      </Tooltip>,
                    ].filter(Boolean)}
                  >
                    <div style={{ width: "100%" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 900, color: "#111827" }}>{name}</div>
                          <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <Tag className="qc-chip" color="blue">
                              <Clock size={12} style={{ marginRight: 6, verticalAlign: -2 }} />
                              {timeLabel}
                            </Tag>
                            <Tag className="qc-chip">
                              <Users size={12} style={{ marginRight: 6, verticalAlign: -2 }} />
                              {party}
                            </Tag>
                            {phone ? (
                              <Tag className="qc-chip">
                                <PhoneIcon size={12} style={{ marginRight: 6, verticalAlign: -2 }} />
                                {phone}
                              </Tag>
                            ) : null}
                            <Tag className="qc-chip" style={{ fontWeight: 900 }}>
                              #{b.id}
                            </Tag>
                          </div>
                        </div>

                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                          {isAssigned ? (
                            <Tag color="green" className="qc-chip" style={{ marginInlineEnd: 0 }}>
                              Assigned
                            </Tag>
                          ) : (
                            <Tag className="qc-chip" style={{ marginInlineEnd: 0 }}>
                              Unassigned
                            </Tag>
                          )}
                        </div>
                      </div>

                      {b.notes ? (
                        <div style={{ marginTop: 10 }}>
                          <Alert type="info" showIcon message={b.notes} style={{ borderRadius: 12 }} />
                        </div>
                      ) : null}
                    </div>
                  </List.Item>
                );
              }}
            />
          )}
        </Drawer>

        {/* Styles */}
        <style>{`
          .qc-head{
            display:flex;
            align-items:flex-start;
            justify-content:space-between;
            gap:12px;
          }
          .qc-right{
            display:flex;
            gap:8px;
            align-items:center;
            justify-content:flex-end;
            flex-wrap:wrap;
          }
          .qc-sub{
            margin-top:6px;
            font-size:12px;
            color:#6b7280;
          }
          .qc-nextUp{
            background:#fbfcfe;
            border:1px solid #eef2f7;
            border-radius:14px;
            padding:10px 12px;
            margin-bottom:10px;
            display:flex;
            align-items:center;
            justify-content:space-between;
            gap:10px;
          }
          .qc-item{
            background:white;
            border-radius:14px;
            border:1px solid #eef2f7;
            padding:12px 12px;
            margin-bottom:10px;
            box-shadow:0 10px 20px rgba(17,24,39,.04);
            cursor:pointer;
          }
          .qc-avatar{
            width:44px;
            height:44px;
            border-radius:16px;
            display:flex;
            align-items:center;
            justify-content:center;
            color:#111827;
            font-weight:900;
            font-size:18px;
          }
          .qc-titleRow{
            display:flex;
            align-items:flex-start;
            justify-content:space-between;
            gap:10px;
            width:100%;
          }
          .qc-titleLeft{
            display:flex;
            align-items:center;
            gap:8px;
            flex-wrap:wrap;
            min-width:0;
          }
          .qc-name{
            font-size:16px;
            color:#111827;
            margin-right:2px;
          }
          .qc-chip{
            border-radius:999px !important;
            font-weight:800 !important;
            letter-spacing:.2px;
            margin-inline-end:0 !important;
          }
          .qc-chip-strong{
            background:#fff !important;
            border:1px solid #e5e7eb !important;
          }
          .qc-chip-booking{
            background:#f0f7ff !important;
            border:1px solid #d6e4ff !important;
            color:#102a56 !important;
          }
          .qc-metaRow{
            display:flex;
            flex-wrap:wrap;
            gap:12px;
          }
          .qc-meta{
            font-size:12px;
            color:#6b7280;
          }
          .qc-actionBtn{
            border-radius:12px !important;
          }
          .qc-miniCount{
            margin:0 !important;
            border-radius:999px !important;
            font-weight:900 !important;
          }

          /* Upcoming */
          .up-sticky{
            position: sticky;
            top: 0;
            z-index: 3;
            background: #fff;
            padding-bottom: 10px;
          }
          .up-controls{
            display:grid;
            gap:10px;
          }
          .up-item{
            border:1px solid #eef2f7;
            border-radius:14px;
            padding:12px;
            margin-bottom:10px;
            background:#fff;
            box-shadow:0 10px 20px rgba(17,24,39,.04);
            cursor:pointer;
          }
          .up-item:hover{
            transform: translateY(-1px);
            transition: transform .15s ease;
          }

          /* iPad max compact tightening */
          @media (max-width: 1024px){
            .qc-head{
              flex-direction:column;
              align-items:flex-start;
            }
            .qc-right{
              width:100%;
              justify-content:flex-start;
            }
            .qc-nextUp{
              flex-direction:column;
              align-items:flex-start;
            }
            .qc-name{
              font-size:15px;
            }
          }
        `}</style>
      </Card>
    </>
  );
}

QueueCard.propTypes = {
  search: PropTypes.string,
  matchingBookings: PropTypes.array.isRequired,
  onSelectBooking: PropTypes.func.isRequired,
  getTableById: PropTypes.func.isRequired,
  waitlist: PropTypes.array.isRequired,
  openAdd: PropTypes.func.isRequired,
  openDetails: PropTypes.func.isRequired,
  notifyGuest: PropTypes.func.isRequired,
  WaitlistRowActions: PropTypes.func.isRequired,
  estimateWaitAI: PropTypes.func,

  // ✅ upcoming drawer (optional)
  upcomingBookings: PropTypes.array,
  onAddBookingToQueue: PropTypes.func,
  onOpenBooking: PropTypes.func,
  onMarkBookingArrived: PropTypes.func,
};

export default QueueCard;
